import { Component, Input, forwardRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { environment } from '../../services/global-environment';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

export interface Personnel {
  id: string;
  username: string;  // 改為 username
  position: string;
  email: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    id: string;
    username: string;
    position: string;
    email: string;
  }[];
}

@Component({
  selector: 'app-form-personnel-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, HttpClientModule, TranslatePipe],
  template: `
    <div class="form-section">
      <label [attr.for]="id">{{ label }}</label>

      <!-- 載入中指示器 -->
      <div *ngIf="isLoading" style="text-align: center; padding: 10px;">
        <span>{{ 'form.personnel_selector.loading' | translate }}</span>
      </div>

      <!-- 錯誤訊息 -->
      <div *ngIf="errorMessage" style="background-color: #f8d7da; color: #721c24; padding: 8px; margin-bottom: 10px; border-radius: 4px; font-size: 14px;">
        {{ errorMessage }}
      </div>

      <!-- 搜尋框 -->
      <input
        *ngIf="!isLoading"
        type="text"
        [id]="id + '_search'"
        [(ngModel)]="searchTerm"
        (ngModelChange)="onSearchChange()"
        [placeholder]="'form.personnel_selector.search_placeholder' | translate"
        class="form-input"
        style="margin-bottom: 10px;"
      />

      <!-- 單選模式 -->
      <select
        *ngIf="!multiSelect && !isLoading"
        [id]="id"
        [name]="name"
        [ngModel]="value"
        (ngModelChange)="onSingleSelectionChange($event)"
        (blur)="onTouched()"
        class="form-input"
      >
        <option value="" disabled>{{ 'form.personnel_selector.placeholder' | translate }}</option>
        <option
          *ngFor="let person of filteredPersonnel"
          [value]="person.id"
        >
          {{ person.username }} ({{ person.email }})
        </option>
      </select>

      <!-- 多選模式 -->
      <div *ngIf="multiSelect && !isLoading" class="multi-select-container">
        <div
          *ngFor="let person of filteredPersonnel"
          class="checkbox-item"
          style="margin-bottom: 8px;"
        >
          <input
            type="checkbox"
            [id]="id + '_' + person.id"
            [checked]="isPersonSelected(person.id)"
            (change)="onMultiSelectionChange(person.id, $event)"
            [name]="name + '_' + person.id"
          />
          <label [for]="id + '_' + person.id" style="margin-left: 8px; font-weight: normal;">
            <strong>{{ person.username }}</strong> - {{ person.position }}
            <br>
            <small style="color: #666;">{{ person.email }}</small>
          </label>
        </div>
      </div>

      <!-- 顯示選中的人員資訊 -->
      <div *ngIf="selectedPersonnelDisplay.length > 0 && !isLoading" class="selected-info" style="margin-top: 10px; padding: 10px; background-color: #e8f4fd; border-radius: 4px; font-size: 14px;">
        <strong>{{ 'form.personnel_selector.selected_count' | translate:{count: selectedPersonnelDisplay.length} }}</strong>
        <div *ngFor="let person of selectedPersonnelDisplay" style="margin-top: 4px;">
          • {{ person.username }} ({{ person.position }}, {{ person.email }})
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormPersonnelSelectorComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormPersonnelSelectorComponent implements ControlValueAccessor, OnInit {
  @Input() label: string = '人員選擇'; // 標籤
  @Input() id: string = ''; // id 用於對應 label
  @Input() name: string = ''; // name 用於表單提交
  @Input() placeholder: string = '請選擇人員'; // 佔位符
  @Input() searchPlaceholder: string = '搜尋人員姓名...'; // 搜尋佔位符
  @Input() multiSelect: boolean = false; // 是否支援多選

  value: string | string[] = ''; // 綁定的值
  searchTerm: string = '';
  personnel: Personnel[] = [];
  filteredPersonnel: Personnel[] = [];
  selectedPersonnelDisplay: Personnel[] = [];
  isLoading: boolean = false;
  errorMessage: string = '';

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(private http: HttpClient, private i18nService: I18nService) {}

  ngOnInit(): void {
    this.initializePersonnel();
  }

  private async initializePersonnel(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      // 從環境變數取得 companyid
      const companyid = environment.companyid;

      if (!companyid) {
        console.warn('FormPersonnelSelector: companyid not available from environment');
        this.handleNoDataResponse();
        return;
      }

      // 調用 API
      const apiUrl = environment.apiUrl || '';
      const url = `${apiUrl}/api/users/getusers?companyId=${companyid}`;

      console.log(`FormPersonnelSelector: Fetching personnel data from ${url}`);

      const response = await this.http.get<ApiResponse>(url).pipe(
        catchError(error => {
          console.error('載入人員資料失敗:', error);
          this.errorMessage = this.i18nService.translate('form.personnel_selector.load_error_fallback');
          return of(null);
        })
      ).toPromise();

      if (response?.success && response.data && Array.isArray(response.data)) {
        // 將 API 資料轉換為元件所需格式
        this.personnel = response.data.map(user => ({
          id: user.id,
          username: user.username,
          position: user.position,
          email: user.email
        }));

        console.log(`FormPersonnelSelector: Loaded ${this.personnel.length} personnel records`);
        this.updateFilteredPersonnel();
      } else {
        console.warn('FormPersonnelSelector: API response not as expected:', response);
        this.handleNoDataResponse();
      }
    } catch (error) {
      console.error('FormPersonnelSelector: Error loading personnel data:', error);
      this.errorMessage = this.i18nService.translate('form.personnel_selector.load_error');
      this.handleNoDataResponse();
    } finally {
      this.isLoading = false;
    }
  }

  private handleNoDataResponse(): void {
    // 當 API 無法取得資料時，可選擇顯示錯誤訊息或使用備用 Mock 資料
    this.personnel = [];
    this.updateFilteredPersonnel();

    // 如果需要使用備用 Mock 資料，可以取消下面的註解
    // this.initializeFallbackMockData();
  }

  // 保留原始 Mock 資料作為參考和備用
  // private initializeFallbackMockData(): void {
  //   // 原始 Mock 人員資料 - 保留作為備用
  //   this.personnel = [
  //     { id: 'P001', name: '王小明', position: '總經理', departmentId: '001', departmentName: '總經理室', email: 'ming.wang@company.com', phone: '02-1234-5678' },
  //     { id: 'P002', name: '李美華', position: '人資經理', departmentId: '002', departmentName: '人力資源部', email: 'meihua.li@company.com' },
  //     { id: 'P003', name: '張志強', position: '招募專員', departmentId: '002-001', departmentName: '招募組', email: 'zhiqiang.zhang@company.com' },
  //     { id: 'P004', name: '陳雅玲', position: '訓練師', departmentId: '002-002', departmentName: '訓練組', email: 'yaling.chen@company.com' },
  //     { id: 'P005', name: '林宇軒', position: '薪酬分析師', departmentId: '002-003', departmentName: '薪酬福利組', email: 'yuxuan.lin@company.com' },
  //     { id: 'P006', name: '黃文龍', position: '財務經理', departmentId: '003', departmentName: '財務部', email: 'wenlong.huang@company.com' },
  //     { id: 'P007', name: '吳佩蓉', position: '會計師', departmentId: '003-001', departmentName: '會計組', email: 'peirong.wu@company.com' },
  //     { id: 'P008', name: '劉建國', position: '出納員', departmentId: '003-002', departmentName: '出納組', email: 'jianguo.liu@company.com' },
  //     { id: 'P009', name: '簡志明', position: '資訊經理', departmentId: '004', departmentName: '資訊部', email: 'zhiming.jian@company.com' },
  //     { id: 'P010', name: '許雅婷', position: '系統工程師', departmentId: '004-001', departmentName: '系統開發組', email: 'yating.xu@company.com' },
  //     { id: 'P011', name: '鄭宏偉', position: '維運工程師', departmentId: '004-002', departmentName: '維運組', email: 'hongwei.zheng@company.com' },
  //     { id: 'P012', name: '蔡淑芬', position: '資安專員', departmentId: '004-003', departmentName: '資安組', email: 'shufen.cai@company.com' },
  //     { id: 'P013', name: '高俊傑', position: '營運經理', departmentId: '005', departmentName: '營運部', email: 'junjie.gao@company.com' },
  //     { id: 'P014', name: '邱麗娟', position: '業務代表', departmentId: '005-001', departmentName: '業務組', email: 'lijuan.qiu@company.com' },
  //     { id: 'P015', name: '范志豪', position: '客服專員', departmentId: '005-002', departmentName: '客服組', email: 'zhihao.fan@company.com' },
  //     { id: 'P016', name: '謝佳琪', position: '研發經理', departmentId: '006', departmentName: '研發部', email: 'jiaqi.xie@company.com' },
  //     { id: 'P017', name: '羅文傑', position: '產品經理', departmentId: '006-001', departmentName: '產品研發組', email: 'wenjie.luo@company.com' },
  //     { id: 'P018', name: '蘇怡君', position: '軟體工程師', departmentId: '006-002', departmentName: '技術研發組', email: 'yijun.su@company.com' }
  //   ];
  //   // 需要將舊的 name 轉換為 username，並移除部門相關欄位
  //   this.personnel = this.personnel.map(person => ({
  //     id: person.id,
  //     username: person.name,  // 將 name 轉換為 username
  //     position: person.position,
  //     email: person.email
  //   }));
  // }

  onSearchChange(): void {
    this.updateFilteredPersonnel();
  }

  private updateFilteredPersonnel(): void {
    let filtered = [...this.personnel];

    // 按搜尋條件篩選 (使用 username 和 position)
    if (this.searchTerm.trim()) {
      filtered = filtered.filter(person =>
        person.username.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        person.position.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    this.filteredPersonnel = filtered;
  }

  onSingleSelectionChange(personId: string): void {
    this.value = personId;
    this.updateSelectedPersonnelDisplay();
    this.onChange(this.value);
  }

  onMultiSelectionChange(personId: string, event: any): void {
    const isChecked = event.target.checked;
    let selectedIds = Array.isArray(this.value) ? [...this.value] : [];

    if (isChecked) {
      if (!selectedIds.includes(personId)) {
        selectedIds.push(personId);
      }
    } else {
      selectedIds = selectedIds.filter(id => id !== personId);
    }

    this.value = selectedIds;
    this.updateSelectedPersonnelDisplay();
    this.onChange(this.value);
  }

  isPersonSelected(personId: string): boolean {
    if (this.multiSelect) {
      return Array.isArray(this.value) && this.value.includes(personId);
    }
    return this.value === personId;
  }

  private updateSelectedPersonnelDisplay(): void {
    if (this.multiSelect && Array.isArray(this.value)) {
      this.selectedPersonnelDisplay = this.personnel.filter(person =>
        this.value.includes(person.id)
      );
    } else if (!this.multiSelect && this.value) {
      const selectedPerson = this.personnel.find(person => person.id === this.value);
      this.selectedPersonnelDisplay = selectedPerson ? [selectedPerson] : [];
    } else {
      this.selectedPersonnelDisplay = [];
    }
  }

  // ControlValueAccessor 實作
  writeValue(value: any): void {
    if (value !== undefined) {
      this.value = value;
      this.updateSelectedPersonnelDisplay();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡處理 disabled 狀態
  }
}
